import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  ExternalLink,
  GitMerge,
  Image,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import type { Product } from "@little-finds/core";
import { api, demo, rpc } from "../api";
import { useEvidencePhotos } from "../useEvidencePhotos";
import { ModerationHistory } from "./ModerationHistory";

type PanelProps = {
  viewerScope: string;
  data: Record<string, any> | null;
  onUpdate: () => Promise<void>;
  catalog: Product[];
};
type Review = (
  kind: string,
  id: string,
  action: string,
  note?: string,
) => Promise<void>;
type Item = Record<string, any>;
type SaveItem = (item: Item) => Promise<boolean>;
const settingTarget = "00000000-0000-4000-8000-000000000000";
const outcomes: Record<string, string> = {
  saw_it: "Saw it",
  couldnt_find: "Couldn’t find it",
  staff_unavailable: "Staff said unavailable",
};
const timestamp = (value?: string) =>
  value ? new Date(value).toLocaleString() : "Time not provided";
const uuidPattern =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

export function AdminPanel({
  data,
  onUpdate,
  catalog,
  viewerScope,
}: PanelProps) {
  const [tab, setTab] = useState("visits");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [historyRevision, setHistoryRevision] = useState(0);
  const { photos, preview, clear } = useEvidencePhotos(`${viewerScope}:${tab}`);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const visits: Item[] = data?.visits ?? [];
  const sources: Item[] = data?.sources ?? [];
  const reports: Item[] = data?.reports ?? [];
  const suggestions: Item[] = data?.suggestions ?? [];
  const imports: Item[] = data?.imported_evidence ?? [];
  const users: Item[] = data?.users ?? [];
  const settings = data?.metrics?.settings ?? {};
  useEffect(() => {
    clear();
  }, [data]);
  const productName = (id: string) =>
    catalog.find((product) => product.id === id)?.name ?? id;

  async function run(
    key: string,
    action: () => Promise<unknown>,
    message: string,
  ) {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action();
      await onUpdate();
      setNotice(message);
      return true;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "The action could not complete. Refresh and try again.",
      );
      return false;
    } finally {
      setBusy("");
    }
  }
  const review: Review = async (kind, id, action, note = "") => {
    if (demo) {
      setError("The demonstration only changes its local sample visits.");
      return;
    }
    await run(
      `${kind}:${id}`,
      () =>
        rpc("review_item", {
          p_kind: kind,
          p_id: id,
          p_action: action,
          p_note: note,
        }),
      "Review saved.",
    );
  };
  async function previewPhoto(evidenceId: string) {
    if (demo) return;
    setBusy(`photo:${evidenceId}`);
    setError("");
    try {
      await preview(evidenceId);
    } catch (e) {
      if ((e as Error).name !== "AbortError")
        setError(e instanceof Error ? e.message : "Photo preview failed.");
    } finally {
      setBusy("");
    }
  }
  function photoControl(id: string, hasPhoto: boolean) {
    if (!hasPhoto || demo) return null;
    return (
      <div className="admin-photo">
        <button
          type="button"
          className="text-button"
          disabled={Boolean(busy)}
          onClick={() => void previewPhoto(id)}
        >
          <Image size={15} />
          {photos[id] ? "Refresh private preview" : "Preview submitted photo"}
        </button>
        {photos[id] && (
          <>
            <img
              src={photos[id]}
              alt="Private submitted shelf photo awaiting a moderation decision"
              onError={() => clear(id)}
            />
            <button
              type="button"
              className="text-button"
              onClick={() => clear(id)}
            >
              Close private preview
            </button>
            <small>
              Private preview stays in this view only. Refresh checks access
              again.
            </small>
          </>
        )}
      </div>
    );
  }
  const tabs = demo
    ? [["visits", "Sample visits", visits.length]]
    : [
        ["visits", "Visits", visits.length],
        ["reports", "Reports", reports.length],
        ["stores", "Store leads", suggestions.length],
        ["imports", "Imported evidence", imports.length],
        ["catalog", "Catalog", (data?.products ?? catalog).length],
        ["sources", "Sources", sources.length],
        ["people", "People", users.length],
        ["history", "History", null],
        ["operations", "Operations", null],
      ];

  return (
    <section
      className="admin-page admin-console"
      aria-label="Community administration"
    >
      <style>{adminStyles}</style>
      {demo && (
        <p className="demo-banner">
          <ShieldCheck size={19} />
          Synthetic demonstration. Reviews change only this browser’s sample
          visits and never publish real evidence.
        </p>
      )}
      <div className="admin-titlebar">
        <div>
          <span className="eyebrow">A LITTLE CARE GOES A LONG WAY</span>
          <h2>The community desk</h2>
        </div>
        <button
          className="button secondary small"
          disabled={Boolean(busy)}
          onClick={() =>
            void run(
              "refresh",
              async () => {
                setHistoryRevision((revision) => revision + 1);
              },
              "The desk is up to date.",
            )
          }
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="admin-notice">
          <Check size={16} />
          {notice}
        </p>
      )}
      <nav className="admin-tabs" aria-label="Review queues">
        {tabs.map(([id, label, count]) => (
          <button
            type="button"
            key={id as string}
            className={tab === id ? "selected" : ""}
            aria-pressed={tab === id}
            onClick={() => setTab(id as string)}
          >
            {label}
            {count !== null && <span>{count}</span>}
          </button>
        ))}
      </nav>
      {!data ? (
        <div className="empty-state">
          <span className="spinner" />
          Loading the community desk…
        </div>
      ) : (
        <>
          {tab === "visits" && (
            <section aria-label="Visit moderation">
              <PanelHeading
                title="The review basket"
                subtitle="Check the exact product, place and visit time. Review the note and photo before publishing content."
              />
              {!visits.length && (
                <Empty text="No visits are waiting for review." />
              )}
              {visits.map((visit) => (
                <article className="review-card admin-card" key={visit.id}>
                  <div className="admin-card-main">
                    <div className="admin-card-heading">
                      <span className="eyebrow">
                        {visit.status === "approved"
                          ? "CONTENT AWAITING REVIEW"
                          : "VISIT AWAITING REVIEW"}
                      </span>
                      <span className="admin-id">{visit.id}</span>
                    </div>
                    <h3>{visit.store_name ?? "Shop visit"}</h3>
                    <p>
                      {timestamp(visit.observed_at)} ·{" "}
                      <code>{visit.store_id}</code>
                    </p>
                    <div className="admin-observations">
                      {(visit.observations ?? []).map(
                        (observation: Item, index: number) => (
                          <span
                            className="review-chip"
                            key={observation.id ?? index}
                          >
                            {productName(observation.product_id)} ·{" "}
                            {outcomes[observation.outcome] ??
                              observation.outcome}
                            {observation.variant
                              ? ` · ${observation.variant}`
                              : ""}
                          </span>
                        ),
                      )}
                    </div>
                    {visit.note && (
                      <blockquote className="admin-submitted-note">
                        {visit.note}
                      </blockquote>
                    )}
                    {photoControl(
                      visit.observations?.[0]?.id,
                      Boolean(visit.photo_path && visit.observations?.[0]?.id),
                    )}
                    <label className="admin-note-label">
                      Review note{" "}
                      <span className="muted">(optional, internal)</span>
                      <textarea
                        maxLength={2000}
                        rows={2}
                        value={notes[visit.id] ?? ""}
                        onChange={(e) =>
                          setNotes((value) => ({
                            ...value,
                            [visit.id]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <div className="review-actions admin-card-actions">
                    <button
                      className="button small"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void run(
                          `visit:${visit.id}`,
                          () =>
                            api.moderate(
                              visit.id,
                              "approved",
                              notes[visit.id] ?? "",
                            ),
                          "Visit and submitted content approved.",
                        )
                      }
                    >
                      <Check size={15} />
                      Approve
                    </button>
                    <button
                      className="button secondary small"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void run(
                          `visit:${visit.id}`,
                          () =>
                            api.moderate(
                              visit.id,
                              "rejected",
                              notes[visit.id] ?? "",
                            ),
                          "Visit rejected. Related reward credit is recalculated.",
                        )
                      }
                    >
                      <X size={15} />
                      Reject
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}
          {!demo && tab === "reports" && (
            <section aria-label="Reported content">
              <PanelHeading
                title="A second look"
                subtitle="Review the reported contribution before removing it. Dismiss keeps the contribution published."
              />
              {!reports.length && (
                <Empty text="No community reports are waiting." />
              )}
              {reports.map((report) => {
                const evidence = report.evidence ?? {};
                const visit = report.visit ?? {};
                return (
                  <article className="review-card admin-card" key={report.id}>
                    <div className="admin-card-main">
                      <span className="eyebrow">COMMUNITY REPORT</span>
                      <h3>{report.store_name ?? "Reported contribution"}</h3>
                      <blockquote className="admin-submitted-note">
                        {report.reason}
                      </blockquote>
                      <p>
                        {productName(evidence.product_id ?? report.evidence_id)}
                        {evidence.outcome &&
                          ` · ${outcomes[evidence.outcome] ?? evidence.outcome}`}{" "}
                        · {timestamp(evidence.observed_at ?? report.created_at)}
                      </p>
                      {(report.content_note ?? visit.note) && (
                        <p className="admin-submitted-note">
                          {report.content_note ?? visit.note}
                        </p>
                      )}
                      <p className="admin-id">Evidence: {report.evidence_id}</p>
                      {photoControl(
                        report.evidence_id,
                        Boolean(report.photo_path ?? visit.photo_path),
                      )}
                      <label className="admin-note-label">
                        Decision note
                        <textarea
                          rows={2}
                          maxLength={2000}
                          value={notes[report.id] ?? ""}
                          onChange={(e) =>
                            setNotes((value) => ({
                              ...value,
                              [report.id]: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <div className="review-actions admin-card-actions">
                      <button
                        className="button secondary small"
                        disabled={Boolean(busy)}
                        onClick={() =>
                          void review(
                            "report",
                            report.id,
                            "remove",
                            notes[report.id],
                          )
                        }
                      >
                        Remove contribution
                      </button>
                      <button
                        className="text-button"
                        disabled={Boolean(busy)}
                        onClick={() =>
                          void review(
                            "report",
                            report.id,
                            "dismiss",
                            notes[report.id],
                          )
                        }
                      >
                        Dismiss report
                      </button>
                    </div>
                  </article>
                );
              })}
            </section>
          )}
          {!demo && tab === "stores" && (
            <section aria-label="Store review">
              <PanelHeading
                title="Put a good shop on the map"
                subtitle="Confirm the physical branch and independently obtained coordinates before approving a suggestion."
              />
              {!suggestions.length && (
                <Empty text="No store suggestions are waiting." />
              )}
              {suggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  busy={Boolean(busy)}
                  review={review}
                />
              ))}
              <MergeStores
                busy={Boolean(busy)}
                review={review}
                stores={data.stores ?? []}
              />
            </section>
          )}
          {!demo && tab === "imports" && (
            <section aria-label="Imported evidence">
              <PanelHeading
                title="Source evidence, thoughtfully checked"
                subtitle="Check product, branch, original observation time and publication rights. A catalog page alone does not prove shelf stock."
              />
              {!imports.length && (
                <Empty text="No imported evidence is waiting." />
              )}
              {imports.map((evidence) => (
                <article className="review-card admin-card" key={evidence.id}>
                  <div className="admin-card-main">
                    <span className="eyebrow">
                      {String(evidence.source_kind).replaceAll("_", " ")}
                    </span>
                    <h3>
                      {evidence.product_id
                        ? productName(evidence.product_id)
                        : `${evidence.stockist_brand ?? "Brand"} stockist lead`}
                    </h3>
                    <p>
                      Branch <code>{evidence.store_id}</code> ·{" "}
                      {timestamp(evidence.observed_at)}
                    </p>
                    <p>
                      {outcomes[evidence.outcome] ?? evidence.outcome}
                      {evidence.variant ? ` · ${evidence.variant}` : ""}
                    </p>
                    <p className="small muted">
                      Received {timestamp(evidence.received_at)}.{" "}
                      {evidence.observed_at
                        ? "Observation time stays unchanged on approval."
                        : "Unknown observation date; cannot become a fresh stock match."}
                    </p>
                    {evidence.source_url && (
                      <SourceLink href={evidence.source_url} />
                    )}
                  </div>
                  <div className="review-actions admin-card-actions">
                    <button
                      className="button small"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void review("evidence", evidence.id, "approved")
                      }
                    >
                      Approve evidence
                    </button>
                    <button
                      className="text-button"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void review("evidence", evidence.id, "rejected")
                      }
                    >
                      Reject
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}
          {!demo && tab === "sources" && (
            <section aria-label="Source permissions">
              <PanelHeading
                title="Know where every lead comes from"
                subtitle="Record the agreement and permitted uses separately. Public access alone is not permission to republish or train."
              />
              <details className="admin-source">
                <summary>
                  <span>
                    <strong>Register a source</strong>
                    <small>
                      New sources begin disabled with all permissions off.
                    </small>
                  </span>
                  <ChevronDown size={17} />
                </summary>
                <SourceIdentityForm
                  busy={Boolean(busy)}
                  save={(item) =>
                    run(
                      "new-source",
                      () => rpc("upsert_source", { p_source: item }),
                      "Source registered with permissions off. Review its permissions before use.",
                    )
                  }
                />
              </details>
              {!sources.length && (
                <Empty text="No sources have been registered." />
              )}
              {sources.map((source) => (
                <SourceCard
                  key={`${source.id}:${source.permission_checked_at}:${source.enabled}:${source.name}:${source.url}:${source.kind}:${source.attribution}:${source.permission_notes}`}
                  source={source}
                  busy={Boolean(busy)}
                  review={review}
                  save={(item) =>
                    run(
                      `source:${source.id}`,
                      () => rpc("upsert_source", { p_source: item }),
                      "Source details saved. Changed URLs or source kinds require fresh permission review.",
                    )
                  }
                />
              ))}
            </section>
          )}
          {!demo && tab === "catalog" && (
            <section aria-label="Product catalog">
              <PanelHeading
                title="Keep every little find distinct"
                subtitle="Add and correct products using their official identity. Bag charms, full-size plush toys and bags keep separate categories."
              />
              <CatalogEditor
                products={data.products ?? catalog}
                busy={Boolean(busy)}
                save={(item) =>
                  run(
                    `product:${item.id ?? "new"}`,
                    () => rpc("upsert_product", { p_product: item }),
                    "Product saved. The shared catalog has been refreshed.",
                  )
                }
              />
            </section>
          )}
          {!demo && tab === "people" && (
            <section aria-label="Contributor trust">
              <PanelHeading
                title="Our helpful neighbors"
                subtitle="Trusted contributors can publish structured observations faster. Their photos and notes still wait for review."
              />
              {!users.length && (
                <Empty text="No contributor accounts to display." />
              )}
              {users.map((user) => (
                <article className="review-card" key={user.id}>
                  <div>
                    <h3>{user.display_name ?? "Contributor"}</h3>
                    <p className="admin-id">{user.id}</p>
                    <p>
                      {user.trusted_contributor
                        ? "Trusted contributor"
                        : "Standard review"}{" "}
                      · Joined {timestamp(user.created_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button secondary small"
                    disabled={Boolean(busy)}
                    onClick={() =>
                      void run(
                        `trust:${user.id}`,
                        () =>
                          rpc("set_contributor_trust", {
                            p_user_id: user.id,
                            p_trusted: !user.trusted_contributor,
                          }),
                        user.trusted_contributor
                          ? "Standard review restored."
                          : "Contributor marked trusted.",
                      )
                    }
                  >
                    <ShieldCheck size={15} />
                    {user.trusted_contributor
                      ? "Remove trusted status"
                      : "Mark as trusted"}
                  </button>
                </article>
              ))}
            </section>
          )}
          {!demo && tab === "history" && (
            <ModerationHistory key={`${viewerScope}:${historyRevision}`} />
          )}
          {!demo && tab === "operations" && (
            <section aria-label="Operations">
              <PanelHeading
                title="Keep things running kindly"
                subtitle="Source ingestion and outgoing alerts can be paused independently while browsing continues."
              />
              <div className="admin-metrics">
                <div>
                  <strong>{data.metrics?.approved_stores ?? 0}</strong>
                  <span>Approved branches</span>
                </div>
                <div>
                  <strong>{data.metrics?.failed_notifications ?? 0}</strong>
                  <span>Failed notifications</span>
                </div>
                <div>
                  <strong>{data.metrics?.stale_sources ?? 0}</strong>
                  <span>Sources needing attention</span>
                </div>
              </div>
              {(["ingestion_paused", "alerts_paused"] as const).map((key) => (
                <article className="review-card" key={key}>
                  <div>
                    <h3>
                      {key === "ingestion_paused"
                        ? "Source ingestion"
                        : "Outgoing alerts"}
                    </h3>
                    <p>{settings[key] ? "Paused" : "Running"}</p>
                  </div>
                  <button
                    type="button"
                    className="button secondary small"
                    disabled={Boolean(busy)}
                    onClick={() =>
                      void review(
                        "settings",
                        settingTarget,
                        "update",
                        JSON.stringify({ [key]: !settings[key] }),
                      )
                    }
                  >
                    {settings[key] ? <Play size={15} /> : <Pause size={15} />}
                    {settings[key] ? "Resume" : "Pause"}
                  </button>
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </section>
  );
}

function PanelHeading({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="admin-section-heading">
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <div className="empty-state">
      <ShieldCheck size={30} />
      <h3>All caught up.</h3>
      <p>{text}</p>
    </div>
  );
}
function SourceLink({ href }: { href: string }) {
  return /^https?:\/\//i.test(href) ? (
    <a className="text-button" href={href} target="_blank" rel="noreferrer">
      Open source <ExternalLink size={13} />
    </a>
  ) : (
    <span className="admin-id">{href}</span>
  );
}

function SuggestionCard({
  suggestion,
  busy,
  review,
}: {
  suggestion: Item;
  busy: boolean;
  review: Review;
}) {
  const store = suggestion.store ?? {};
  const [latitude, setLatitude] = useState(String(store.latitude ?? ""));
  const [longitude, setLongitude] = useState(String(store.longitude ?? ""));
  const [timezone, setTimezone] = useState(store.timezone ?? "");
  const [source, setSource] = useState(store.coordinate_source ?? "");
  const [permission, setPermission] = useState(
    store.coordinate_permission_record ?? "",
  );
  const [verified, setVerified] = useState(false);
  return (
    <article className="review-card admin-card">
      <form
        className="stack admin-card-main"
        onSubmit={(e) => {
          e.preventDefault();
          void review(
            "suggestion",
            suggestion.id,
            "approve",
            JSON.stringify({
              latitude: Number(latitude),
              longitude: Number(longitude),
              timezone: timezone.trim(),
              coordinate_source: source.trim(),
              coordinate_permission_record: permission.trim(),
            }),
          );
        }}
      >
        <div>
          <span className="eyebrow">SUGGESTED BRANCH</span>
          <h3>{store.name ?? "Suggested shop"}</h3>
          <p>
            {store.address}
            <br />
            {store.city}, {store.state} {store.zip}
          </p>
          {store.website && <SourceLink href={store.website} />}
        </div>
        <div className="form-grid">
          <label>
            Verified latitude
            <input
              type="number"
              step="any"
              min={-90}
              max={90}
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              required
            />
          </label>
          <label>
            Verified longitude
            <input
              type="number"
              step="any"
              min={-180}
              max={180}
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              required
            />
          </label>
        </div>
        <label>
          Branch timezone
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Chicago"
            required
            maxLength={80}
          />
        </label>
        <label>
          Independent coordinate source
          <input
            type="url"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="https://retailer.example/verified-location"
            required
          />
        </label>
        <label>
          Permission or verification record
          <input
            value={permission}
            onChange={(e) => setPermission(e.target.value)}
            placeholder="Reference to the coordinate grant or first-party verification"
            required
            maxLength={1000}
          />
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
            required
          />
          <span>
            I checked this physical branch, its coordinates and permission to
            retain them.
          </span>
        </label>
        <div className="review-actions">
          <button className="button small" disabled={busy || !verified}>
            <Check size={15} />
            Approve branch
          </button>
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => void review("suggestion", suggestion.id, "reject")}
          >
            Dismiss suggestion
          </button>
        </div>
      </form>
    </article>
  );
}

function SourceCard({
  source,
  busy,
  review,
  save,
}: {
  source: Item;
  busy: boolean;
  review: Review;
  save: SaveItem;
}) {
  const [enabled, setEnabled] = useState(Boolean(source.enabled));
  const [retrieval, setRetrieval] = useState(Boolean(source.retrieval_allowed));
  const [redistribution, setRedistribution] = useState(
    Boolean(source.redistribution_allowed),
  );
  const [training, setTraining] = useState(Boolean(source.training_allowed));
  const [retention, setRetention] = useState(
    source.retention_days === null || source.retention_days === undefined
      ? ""
      : String(source.retention_days),
  );
  const [hosts, setHosts] = useState((source.allowed_hosts ?? []).join("\n"));
  const [attribution, setAttribution] = useState(source.attribution ?? "");
  const [permission, setPermission] = useState(source.permission_notes ?? "");
  return (
    <details className="admin-source">
      <summary>
        <span>
          <strong>{source.name}</strong>
          <small>
            {source.enabled ? "Enabled" : "Disabled"} ·{" "}
            {source.last_error
              ? "Last import failed"
              : source.last_success_at
                ? `Last success ${timestamp(source.last_success_at)}`
                : "No successful import"}
          </small>
        </span>
        <ChevronDown size={17} />
      </summary>
      <details className="admin-identity">
        <summary>Edit source identity</summary>
        <SourceIdentityForm source={source} busy={busy} save={save} />
      </details>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void review(
            "source",
            source.id,
            "update_permissions",
            JSON.stringify({
              enabled,
              retrieval_allowed: retrieval,
              redistribution_allowed: redistribution,
              training_allowed: training,
              retention_days: retention === "" ? null : Number(retention),
              allowed_hosts: hosts
                .split(/[\n,]/)
                .map((host: string) => host.trim().toLowerCase())
                .filter(Boolean),
              attribution: attribution.trim(),
              permission_notes: permission.trim(),
            }),
          );
        }}
      >
        <SourceLink href={source.url} />
        {source.last_error && (
          <p className="form-error">Last source error: {source.last_error}</p>
        )}
        <div className="admin-permissions">
          {[
            ["Enable source", enabled, setEnabled],
            ["Allow retrieval", retrieval, setRetrieval],
            ["Allow redistribution", redistribution, setRedistribution],
            ["Allow training", training, setTraining],
          ].map(([label, checked, setter]) => (
            <label className="check-row" key={label as string}>
              <input
                type="checkbox"
                checked={checked as boolean}
                onChange={(e) =>
                  (setter as (value: boolean) => void)(e.target.checked)
                }
              />
              <span>{label as string}</span>
            </label>
          ))}
        </div>
        <label>
          Retention period in days
          <input
            type="number"
            min={1}
            max={36500}
            value={retention}
            onChange={(e) => setRetention(e.target.value)}
            placeholder="Blank only when the grant permits indefinite retention"
          />
        </label>
        <label>
          Permitted source hosts
          <textarea
            rows={2}
            value={hosts}
            onChange={(e) => setHosts(e.target.value)}
            placeholder={"retailer.example\nfeeds.retailer.example"}
          />
        </label>
        <label>
          Required attribution
          <input
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
            maxLength={1000}
          />
        </label>
        <label>
          Agreement and permitted uses
          <textarea
            rows={3}
            required
            value={permission}
            onChange={(e) => setPermission(e.target.value)}
            placeholder="Identify the permission record, what it covers, any expiry and the reviewer."
            maxLength={10000}
          />
        </label>
        <p className="small muted">
          A source training permission does not replace contributor consent. No
          model is enabled here.
        </p>
        <div className="review-actions">
          <button className="button small" disabled={busy}>
            Save permissions
          </button>
          <button
            type="button"
            className="text-button"
            disabled={busy || !source.enabled}
            onClick={() => void review("source", source.id, "disable")}
          >
            Disable source now
          </button>
        </div>
      </form>
    </details>
  );
}

function SourceIdentityForm({
  source,
  busy,
  save,
}: {
  source?: Item;
  busy: boolean;
  save: SaveItem;
}) {
  const [name, setName] = useState(source?.name ?? "");
  const [url, setUrl] = useState(source?.url ?? "");
  const [kind, setKind] = useState(source?.kind ?? "retailer");
  const [attribution, setAttribution] = useState(source?.attribution ?? "");
  const [permission, setPermission] = useState(source?.permission_notes ?? "");
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const saved = await save({
          ...(source?.id ? { id: source.id } : {}),
          name: name.trim(),
          url: url.trim(),
          kind,
          attribution: attribution.trim(),
          permission_notes: permission.trim(),
        });
        if (saved && !source) {
          setName("");
          setUrl("");
          setAttribution("");
          setPermission("");
        }
      }}
    >
      <label>
        Source name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={160}
        />
      </label>
      <div className="form-grid">
        <label>
          HTTPS source URL
          <input
            type="url"
            pattern="https://.+"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://retailer.example"
          />
        </label>
        <label>
          Source kind
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="retailer">Retailer</option>
            <option value="public_mention">Public mention</option>
            <option value="stockist">Stockist directory</option>
            <option value="community">Community</option>
          </select>
        </label>
      </div>
      <label>
        Attribution
        <input
          value={attribution}
          onChange={(e) => setAttribution(e.target.value)}
          maxLength={1000}
        />
      </label>
      <label>
        Permission research notes
        <textarea
          value={permission}
          onChange={(e) => setPermission(e.target.value)}
          rows={2}
          maxLength={10000}
          placeholder="Record known evidence or note that permission still needs review."
        />
      </label>
      <p className="small muted">
        {source
          ? "Changing the URL or kind disables this source and clears its use permissions. Review permissions again afterward."
          : "Registration does not authorize retrieval, publication or training. Review each permission after creating the source."}
      </p>
      <button className="button small" disabled={busy}>
        {source ? "Save source details" : "Register disabled source"}
      </button>
    </form>
  );
}

type ProductDraft = {
  id?: string;
  name: string;
  slug: string;
  brand: Product["brand"];
  category: Product["category"];
  manufacturer_code: string;
  url: string;
  color: string;
  illustration: string;
  active: boolean;
};
const productDraft = (product?: Item): ProductDraft => ({
  ...(product?.id ? { id: product.id } : {}),
  name: product?.name ?? "",
  slug: product?.slug ?? "",
  brand: product?.brand ?? "Jellycat",
  category: product?.category ?? "plush",
  manufacturer_code: product?.manufacturer_code ?? "",
  url: product?.url ?? "",
  color: product?.color ?? "#cde4d4",
  illustration: product?.illustration ?? "sparkle",
  active: product?.active ?? true,
});

function CatalogEditor({
  products,
  busy,
  save,
}: {
  products: Item[];
  busy: boolean;
  save: SaveItem;
}) {
  const [draft, setDraft] = useState<ProductDraft>(() => productDraft());
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  return (
    <article className="review-card admin-card">
      <form
        className="stack admin-card-main"
        onSubmit={async (e) => {
          e.preventDefault();
          const saved = await save({
            ...draft,
            name: draft.name.trim(),
            slug: draft.slug.trim(),
            manufacturer_code: draft.manufacturer_code.trim(),
            url: draft.url.trim(),
            illustration: draft.illustration.trim() || "sparkle",
          });
          if (saved) setDraft(productDraft());
        }}
      >
        <label>
          Product to edit
          <select
            value={draft.id ?? ""}
            onChange={(e) =>
              setDraft(
                productDraft(
                  products.find((product) => product.id === e.target.value),
                ),
              )
            }
          >
            <option value="">Add a new product</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.brand} · {product.name}
                {product.active === false ? " (retired)" : ""}
              </option>
            ))}
          </select>
        </label>
        {draft.id && (
          <p className="admin-id">Canonical product ID: {draft.id}</p>
        )}
        <div className="form-grid">
          <label>
            Product name
            <input
              value={draft.name}
              onChange={(e) => set("name", e.target.value)}
              required
              maxLength={120}
            />
          </label>
          <label>
            Unique slug
            <input
              value={draft.slug}
              onChange={(e) => set("slug", e.target.value)}
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              maxLength={100}
              placeholder="amuseables-peanut"
            />
          </label>
        </div>
        <div className="form-grid">
          <label>
            Brand
            <select
              value={draft.brand}
              onChange={(e) => set("brand", e.target.value as Product["brand"])}
            >
              <option>NeeDoh</option>
              <option>Jellycat</option>
            </select>
          </label>
          <label>
            Product category
            <select
              value={draft.category}
              onChange={(e) =>
                set("category", e.target.value as Product["category"])
              }
            >
              <option value="sensory">Sensory toy</option>
              <option value="plush">Plush toy</option>
              <option value="bag_charm">Bag charm</option>
              <option value="bag">Bag</option>
            </select>
          </label>
        </div>
        <label>
          Official HTTPS product URL
          <input
            type="url"
            pattern="https://.+"
            value={draft.url}
            onChange={(e) => set("url", e.target.value)}
            required
            placeholder="https://us.jellycat.com/product-name/"
          />
        </label>
        <label>
          Manufacturer code <span className="muted">(optional)</span>
          <input
            value={draft.manufacturer_code}
            onChange={(e) => set("manufacturer_code", e.target.value)}
            maxLength={100}
            placeholder="Leave blank when not verified"
          />
        </label>
        <div className="form-grid">
          <label>
            Illustration color
            <input
              type="color"
              value={
                /^#[0-9a-f]{6}$/i.test(draft.color) ? draft.color : "#cde4d4"
              }
              onChange={(e) => set("color", e.target.value)}
            />
          </label>
          <label>
            Illustration
            <select
              value={draft.illustration}
              onChange={(e) => set("illustration", e.target.value)}
            >
              {[
                ...new Set([
                  "sparkle",
                  "popsicle",
                  "jelly",
                  "cat",
                  "cube",
                  "peanut",
                  "octopus",
                  "dragon",
                  "coral",
                  "capybara",
                  "cake",
                  draft.illustration,
                ]),
              ].map((illustration) => (
                <option key={illustration} value={illustration}>
                  {illustration}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="check-row">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => set("active", e.target.checked)}
          />
          <span>
            Active in the public catalog
            <small>
              Turn off to retire this product without deleting its history.
            </small>
          </span>
        </label>
        <p className="small muted">
          Jellycat bag charms added in the bag charm category match the saved
          “Any Jellycat bag charm” selection automatically. Keep bags and plush
          versions as separate products.
        </p>
        <div className="review-actions">
          <button className="button small" disabled={busy}>
            <Check size={15} />
            {draft.id ? "Save product changes" : "Add product"}
          </button>
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => setDraft(productDraft())}
          >
            Clear editor
          </button>
        </div>
      </form>
    </article>
  );
}

function MergeStores({
  busy,
  review,
  stores,
}: {
  busy: boolean;
  review: Review;
  stores: Item[];
}) {
  const [duplicate, setDuplicate] = useState("");
  const [canonical, setCanonical] = useState("");
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState("");
  return (
    <details className="admin-source">
      <summary>
        <span>
          <strong>
            <GitMerge size={16} /> Combine duplicate branches
          </strong>
          <small>Keep one canonical location for the same physical shop.</small>
        </span>
        <ChevronDown size={17} />
      </summary>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (duplicate === canonical) return;
          void review("store_merge", duplicate.trim(), canonical.trim(), note);
        }}
      >
        <p className="small muted">
          Combining branches moves visits, evidence and notifications to the
          canonical branch and recalculates duplicate daily rewards. Branches
          must share a timezone.
        </p>
        <datalist id="admin-known-stores">
          {stores.map((store) => (
            <option key={store.id} value={store.id}>
              {store.name} · {store.city}, {store.state}
            </option>
          ))}
        </datalist>
        <label>
          Duplicate branch UUID
          <input
            list="admin-known-stores"
            pattern={uuidPattern}
            value={duplicate}
            onChange={(e) => setDuplicate(e.target.value)}
            required
          />
        </label>
        <label>
          Canonical branch UUID
          <input
            list="admin-known-stores"
            pattern={uuidPattern}
            value={canonical}
            onChange={(e) => setCanonical(e.target.value)}
            required
          />
        </label>
        <label>
          Merge note
          <textarea
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How you confirmed these records refer to the same shop"
            required
          />
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            required
          />
          <span>
            I verified these records describe the same physical branch.
          </span>
        </label>
        <button
          className="button secondary small"
          disabled={busy || !checked || !duplicate || duplicate === canonical}
        >
          <GitMerge size={16} />
          Combine branches
        </button>
      </form>
    </details>
  );
}

const adminStyles = `
.admin-console .admin-history-list{list-style:none;padding:0;margin:18px 0}.admin-console .admin-history-list h3{overflow-wrap:anywhere}
.admin-console .admin-titlebar{display:flex;justify-content:space-between;align-items:center;gap:20px}.admin-console .admin-titlebar h2{font-size:25px;margin-bottom:8px}.admin-console .admin-tabs{display:flex;gap:7px;flex-wrap:wrap;margin:22px 0 28px;padding-bottom:15px;border-bottom:1px solid var(--line)}.admin-console .admin-tabs button{display:flex;gap:8px;align-items:center;background:#fffdf7;border:1px solid var(--line);border-radius:8px;padding:10px 12px;font-size:11px}.admin-console .admin-tabs button.selected{background:#eaf0e2;border-color:#b9c9ab;color:#526c56}.admin-console .admin-tabs span{font-size:10px;opacity:.7}.admin-console .admin-section-heading{max-width:690px;margin-bottom:22px}.admin-console .admin-section-heading h2{font-size:19px;margin-bottom:8px}.admin-console .admin-section-heading p{font-size:12px;line-height:1.7;color:var(--muted);margin:0}.admin-console .admin-notice{display:flex;gap:9px;align-items:center;padding:12px 15px;border-radius:8px;background:#ebf2e3;font-size:12px}.admin-console .admin-card{align-items:flex-start}.admin-console .admin-card-main{flex:1;min-width:0}.admin-console .admin-card-heading{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}.admin-console .admin-card-heading .eyebrow{margin-bottom:0}.admin-console .admin-id{font-size:10px;color:var(--muted);overflow-wrap:anywhere}.admin-console code{font-size:10px;overflow-wrap:anywhere}.admin-console .admin-card-actions{flex-wrap:wrap;flex-shrink:0}.admin-console .admin-submitted-note{background:#f4f3e9;border-left:3px solid #c7d3b7;margin:13px 0;padding:13px 15px;font-size:12px;line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere}.admin-console .admin-note-label{display:block;margin-top:14px;font-size:11px}.admin-console .admin-note-label textarea{display:block;width:100%;margin-top:8px;resize:vertical;border:1px solid var(--line);border-radius:7px;background:#fffef9;padding:10px;font-size:12px}.admin-console .admin-photo{display:flex;flex-direction:column;align-items:flex-start;gap:10px;margin-top:13px}.admin-console .admin-photo img{width:100%;max-width:560px;max-height:430px;object-fit:contain;border:1px solid var(--line);border-radius:8px;background:#f4f0e4}.admin-console .admin-photo small{font-size:10px;color:var(--muted)}.admin-console .admin-source{background:#fffdf7;border:1px solid var(--line);border-radius:11px;margin:15px 0;padding:0 20px}.admin-console .admin-source>summary{display:flex;justify-content:space-between;gap:20px;align-items:center;cursor:pointer;padding:19px 0;list-style:none}.admin-console .admin-source>summary strong{display:flex;align-items:center;gap:8px;font-size:13px}.admin-console .admin-source>summary small{display:block;font-size:11px;color:var(--muted);margin-top:7px;line-height:1.5}.admin-console .admin-source .stack{padding:3px 0 23px}.admin-console .admin-permissions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.admin-console .admin-permissions .check-row{align-items:center}.admin-console .review-card .button{align-self:flex-start}.admin-console .stack input,.admin-console .stack select{max-width:100%}.admin-console .admin-observations{margin-top:9px}.admin-console .stack textarea{resize:vertical}.admin-console .stack .small{line-height:1.7}.admin-console .review-actions{flex-wrap:wrap}@media(max-width:900px){.admin-console .admin-card{flex-direction:column}.admin-console .admin-card-main{width:100%}.admin-console .admin-card-actions{width:100%;justify-content:flex-start}}@media(max-width:600px){.admin-console .admin-tabs button{padding:10px;font-size:10px}.admin-console .admin-permissions{grid-template-columns:1fr}.admin-console .admin-titlebar{align-items:flex-start}.admin-console .admin-titlebar h2{font-size:22px}.admin-console .admin-source{padding:0 15px}.admin-console .admin-metrics{flex-wrap:wrap}.admin-console .admin-metrics>div{min-width:100px}.admin-console .admin-card-heading{align-items:flex-start;flex-direction:column}}
`;
